import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { Rating } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import SentimentSatisfiedAltRoundedIcon from '@mui/icons-material/SentimentSatisfiedAltRounded';
import SentimentDissatisfiedRoundedIcon from '@mui/icons-material/SentimentDissatisfiedRounded';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import { isNativeApp } from 'utils/nativeApp';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Api from 'api';
import { FeedbackModalJob } from 'components/FeedbackModal';
import SuccessModal from './components/SuccessModal';
import ReviewAskModal, { ReviewAskPayload } from 'components/ReviewAskModal';
import { SimpleModal } from 'components/Modal';
import { setChatbaseUiHidden } from 'components/ChatWidget';
import FixedLoader from 'components/FixedLoader';
import FixedError from 'components/FixedError';
import ApiError from 'api/ApiError';
import styled, { createGlobalStyle } from 'styled-components';
import {
  markTalentFeedbackHomePromptShown,
  markTalentFeedbackHomePromptSuppressed,
  setTalentFeedbackInQueue,
} from 'utils/talentFeedbackSession';
import { acceptedFeedback, definitelyUnsubmitted, feedbackDeliveryMessage, FeedbackIdentity, AcceptedFeedback, FeedbackActor } from './feedbackSubmission';
import useFeedbackActor from './useFeedbackActor';
import { resolveFeedbackViewOnlyMode, canCompleteTalentSurvey } from '../../../utils/feedbackEditEligibility';

type FeedbackLocationState = {
  job?: FeedbackModalJob;
  jobId?: number | string;
  fromJobCompleted?: boolean;
  /** T2-6961 view-only of submitted feedback */
  viewOnly?: boolean | number | string;
  /** Legacy aliases — treated as viewOnly */
  editMode?: boolean | number | string;
  isEdit?: boolean | number | string;
};

/** Exit React feedback overlay and reload the F7 talent calendar (home). */
function returnToTalentCalendar(isCurrent: () => boolean) {
  if (!isCurrent()) return;
  setTalentFeedbackInQueue(false);
  markTalentFeedbackHomePromptSuppressed();
  try {
    safeLocalStorage.removeItem('pageData');
  } catch { /* ignore */ }
  const exit = (window as any).TempStars?.App?.exitReactStyles;
  if (typeof exit === 'function') {
    exit(() => {
      if (!isCurrent()) return;
      (window as any).TempStars?.Hygienist?.Router?.reloadPage?.('home');
    });
    return;
  }
  (window as any).TempStars?.Hygienist?.Router?.reloadPage?.('home');
}

type FutureCollaboration = 'love' | 'fine' | 'prefer-not' | null;

const normalizeRating = (value?: number | string | null) => {
  const rating = Number(value);
  return Number.isFinite(rating) ? rating : 0;
};

const modalFont = "var(--fontMain, 'Poppins', sans-serif)";

const WiderModalStyle = createGlobalStyle`
  .feedback-talent-modal [class*="window"] {
    max-width: 42rem !important;
    padding: 0 !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  color: #202939;
  font-family: ${modalFont};
`;

const ModalHeaderBand = styled.div`
  background: #f5f7fa;
  padding: 10px 20px 8px;
  border-bottom: 1px solid #edf0f5;
`;
const ModalHeaderInner = styled.div`
  display: flex; align-items: center; justify-content: space-between;
`;
const HeaderLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const HeaderTitle = styled.span`
  color: #586174;
  font-size: 0.88rem;
  font-weight: 400;
  font-family: ${modalFont};
`;

const ModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 14px 20px 20px;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
`;

const QuestionTitle = styled.h2`
  margin: 0 0 14px 0;
  color: #1d2433;
  font-size: 1.38rem;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.01em;
  font-family: ${modalFont};
`;
const StarRow = styled.div`display: flex; gap: 10px; margin-bottom: 8px;`;
interface StarBtnProps { $active: boolean; $readOnly?: boolean; }
const StarButton = styled.div<StarBtnProps>`
  flex: 1;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => ($active ? '#dde3ec' : '#e4e8ef')};
  background: #fff;
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  pointer-events: ${({ $readOnly }) => ($readOnly ? 'none' : 'auto')};
  transition: border-color 0.15s ease;
  &:hover { border-color: ${({ $readOnly }) => ($readOnly ? '#e4e8ef' : '#d1d9e6')}; }
`;
const LabelsRow = styled.div`display: flex; justify-content: space-between; margin-bottom: 2px;`;
const ScaleLabel = styled.div`
  color: #697386;
  font-size: 0.96rem;
  font-weight: 400;
  line-height: 1.25;
  font-family: ${modalFont};
`;
const Divider = styled.div`border-top: 1px dashed #e7ebf2; margin: 18px 0;`;
const ErrorText = styled.div`color: #FB7672; font-size: 0.8rem; margin-top: 4px;`;

const CollabTitle = styled.div`
  color: #6f7787;
  margin-bottom: 10px;
  font-size: 1rem;
  line-height: 1.35;
  font-weight: 400;
  font-family: ${modalFont};
`;
const CollabRow = styled.div`display: flex; gap: 8px;`;
interface CardProps { $selected: boolean; $readOnly?: boolean; }
const CollabCard = styled.div<CardProps>`
  flex: 1;
  min-height: 82px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  padding: 12px 10px 10px;
  border-radius: 12px;
  border: 1px solid ${({ $selected }) => ($selected ? '#8faedd' : '#e4e8ef')};
  background-color: ${({ $selected }) => ($selected ? '#f5f9ff' : '#fff')};
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.03);
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  pointer-events: ${({ $readOnly }) => ($readOnly ? 'none' : 'auto')};
  transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
  &:hover {
    border-color: ${({ $selected, $readOnly }) => (
      $readOnly ? ($selected ? '#8faedd' : '#e4e8ef') : ($selected ? '#7fa4d8' : '#cfd7e6')
    )};
    box-shadow: ${({ $readOnly }) => ($readOnly ? '0 1px 2px rgba(16, 24, 40, 0.03)' : '0 2px 4px rgba(16, 24, 40, 0.05)')};
  }
`;
const OptionIcon = styled.span`
  display: flex; align-items: center; justify-content: center;
  height: 24px; font-size: 1.2rem; line-height: 1; margin-bottom: 8px;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
`;
const CardLabel = styled.div<CardProps>`
  max-width: 146px;
  color: #202939;
  font-size: 0.88rem;
  line-height: 1.3;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  letter-spacing: -0.01em;
  font-family: ${modalFont};
`;

const BlockWarning = styled.div`
  color: #FB7672;
  font-size: 0.82rem;
  line-height: 1.35;
  margin-top: 8px;
  font-family: ${modalFont};
`;

const WriteReviewLabel = styled.div`
  margin-bottom: 8px;
  color: #202939;
  font-size: 1rem;
  font-weight: 500;
  font-family: ${modalFont};
`;
const ReviewTextarea = styled.textarea`
  width: 100%;
  min-height: 84px;
  padding: 12px 16px;
  border: 1px solid #e4e8ef;
  border-radius: 10px;
  box-sizing: border-box;
  color: #202939;
  font-size: 0.92rem;
  line-height: 1.45;
  font-family: ${modalFont};
  resize: none;
  outline: none;
  background: #fff;
  &:hover { border-color: #d4dbe7; }
  &:focus { border-color: #d4dbe7; }
  &::placeholder {
    color: #9aa3b2;
    font-style: italic;
    font-size: 0.86rem;
    opacity: 1;
    font-family: ${modalFont};
  }
`;

const SectionLabel = styled.div`
  margin-bottom: 8px;
  color: #6f7787;
  font-size: 0.88rem;
  font-weight: 400;
  line-height: 1.35;
  font-family: ${modalFont};
`;
const SkillCard = styled.div`
  display: flex; align-items: center; background: #fff; border-radius: 12px;
  padding: 14px 16px; margin-bottom: 8px; gap: 12px; border: 1px solid #edf0f5;
`;
interface IconCircleProps { $bg: string; $color: string; }
const IconCircle = styled.div<IconCircleProps>`
  width: 38px; height: 38px; border-radius: 10px; background: ${({ $bg }) => $bg};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  svg { font-size: 18px; color: ${({ $color }) => $color}; }
`;
const SkillName = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  color: #202939;
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.01em;
  font-family: ${modalFont};
`;

const SubmitContainer = styled.div`
  position: sticky;
  bottom: -20px;
  z-index: 3;
  margin: 14px -20px -20px;
  padding: 12px 20px 20px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 22%, #fff 100%);
  border-top: 1px solid #edf0f5;
`;
const SubmitBtn = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  font-family: ${modalFont};
  color: #fff;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  background: ${({ disabled }) => disabled ? '#ccc' : '#0B4598'};
  transition: background 0.2s;
  &:hover { background: ${({ disabled }) => disabled ? '#ccc' : '#083a7a'}; }
`;
// T2-6191: de-emphasized "Skip for now" control — light-grey chevron in the
// bottom-right (matches the DO feedback form treatment).
const SkipRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const SkipChevronBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #9aa3b2;
  cursor: pointer;

  svg {
    width: 22px;
    height: 22px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const talentSkills = [
  { key: 'friendly', label: 'Friendly & Professional', icon: <GroupIcon />, bg: '#ffebee', color: '#FB7672' },
  { key: 'infection', label: 'Infection Control & Workplace Compliance', icon: <SearchIcon />, bg: '#f3e8ff', color: '#9932CC' },
  { key: 'tools', label: 'Tools & Equipment', icon: <BuildIcon />, bg: '#e8f0fe', color: '#0B4598' },
  { key: 'setup', label: 'Preparation & Setup for Success', icon: <AssignmentIcon />, bg: '#e6f9f0', color: '#13CA82' },
];

function hasTalentFeedbackJobPayload(j: FeedbackModalJob | null): j is FeedbackModalJob {
  if (!j || j.id == null) return false;
  if (!Array.isArray(j.shifts) || j.shifts.length === 0) return false;
  const name = j.location?.practiceName || j.officeName;
  return Boolean(name);
}

type PendingTalentReview = { jobId?: number | string };

export function normalizePendingTalentReviewResponse(pending: unknown): PendingTalentReview[] {
  if (Array.isArray(pending)) return pending;
  if (Array.isArray((pending as any)?.data)) return (pending as any).data;
  return [];
}

export function selectNextPendingTalentReview(
  pendingList: PendingTalentReview[],
  excludeJobId?: number | string,
): PendingTalentReview | undefined {
  const excluded = Number(excludeJobId);
  return pendingList.find((row) => {
    const id = Number(row?.jobId);
    return Number.isFinite(id)
      && id > 0
      && (!Number.isFinite(excluded) || id !== excluded);
  });
}

export function isTalentFeedbackSubmitDisabled(
  overallRating: number | null,
  isSubmitting: boolean,
  formSubmitted: boolean,
): boolean {
  return !overallRating || isSubmitting || formSubmitted;
}

export function shouldRenderTalentFeedbackForm(
  showSuccessModal: boolean,
  formSubmitted: boolean,
  viewOnly = false,
): boolean {
  // View-only keeps the form visible until Done navigates away.
  if (viewOnly) return !showSuccessModal;
  return !showSuccessModal && !formSubmitted;
}

type FeedbackNavigationHistory = {
  push: (path: string) => void;
  replace: (location: string | { pathname: string }) => void;
};

export function navigateToTalentHomeAfterFeedback(
  history: FeedbackNavigationHistory,
  tempStars = (window as any).TempStars,
): void {
  const goReactHome = tempStars?.App?.goReact;
  if (typeof goReactHome === 'function') {
    history.push('loading');
    goReactHome('hygienistHome');
    return;
  }
  history.replace({ pathname: 'hygienistHome' });
}

export default function FeedbackPage() {
  const location = useLocation();
  const { actor, currentActor } = useFeedbackActor();
  const state = (location.state || {}) as FeedbackLocationState;
  const routeKey = JSON.stringify([location.key, location.pathname, location.search,
    state.jobId, state.job?.id, resolveFeedbackViewOnlyMode({ state, search: location.search })]);
  const bindingRef = useRef({ routeKey, actorKey: actor?.key || '', invalidated: false, sequence: 0 });
  if (bindingRef.current.routeKey !== routeKey) {
    bindingRef.current = { routeKey, actorKey: actor?.key || '', invalidated: false, sequence: bindingRef.current.sequence + 1 };
  } else if (bindingRef.current.actorKey && bindingRef.current.actorKey !== actor?.key) {
    // Never reuse a previous account's navigation payload during reconciliation,
    // even if the other session mirrors catch up on a later render.
    bindingRef.current.invalidated = true;
  } else if (!bindingRef.current.actorKey && actor) {
    bindingRef.current.actorKey = actor.key;
  }
  const binding = bindingRef.current;
  const isRouteCurrent = () => bindingRef.current === binding && !binding.invalidated
    && !!binding.actorKey && currentActor()?.key === binding.actorKey;
  if (!actor || !isRouteCurrent()) return <FixedLoader loading />;
  // A new route/actor gets a new state owner. No prior notes or callbacks can
  // survive until a passive effect clears them.
  return <FeedbackVisit key={binding.sequence} actor={actor} isRouteCurrent={isRouteCurrent} />;
}

function FeedbackVisit({ actor, isRouteCurrent }: { actor: FeedbackActor; isRouteCurrent: () => boolean }) {
  const history = useHistory();
  const location = useLocation();
  const locationState = (location.state || {}) as FeedbackLocationState;
  const viewOnly = resolveFeedbackViewOnlyMode({
    state: locationState,
    search: location.search,
  });
  const [job, setJob] = useState<FeedbackModalJob | null>(null);
  const [isLoadingJob, setIsLoadingJob] = useState(true);
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const starRefs = useRef<(HTMLDivElement | null)[]>([]);
  const collabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const collabOptions: FutureCollaboration[] = ['love', 'fine', 'prefer-not'];
  const native = isNativeApp();
  const [ratingTouched, setRatingTouched] = useState(false);
  const [skillRatings, setSkillRatings] = useState<Record<string, number | null>>({ friendly: null, infection: null, tools: null, setup: null });
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [futureCollab, setFutureCollab] = useState<FutureCollaboration>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [hydrateError, setHydrateError] = useState<string | null>(null);
  // T2-6899: latch outside per-survey resetForm — one ask per feedback run.
  const latchedReviewAskRef = useRef<ReviewAskPayload | null>(null);
  const [latchedReviewAsk, setLatchedReviewAsk] = useState<ReviewAskPayload | null>(null);
  const [showReviewAskModal, setShowReviewAskModal] = useState(false);

  type SubmissionPhase = 'idle' | 'submitting' | 'checking' | 'unknown' | 'accepted';
  const [submissionPhase, setSubmissionPhase] = useState<SubmissionPhase>('checking');
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(null);
  const [acceptedMessage, setAcceptedMessage] = useState<string | null>(null);
  const operationRef = useRef(false);
  const continuedRef = useRef(false);
  const actorKey = actor.key;
  const renderedVisitKey = `${location.key || ''}:${actorKey}`;
  const visitRef = useRef({ key: renderedVisitKey, generation: 0 });
  if (visitRef.current.key !== renderedVisitKey) {
    visitRef.current = { key: renderedVisitKey, generation: visitRef.current.generation + 1 };
  }
  // Passive-effect cleanup can run after a just-resolved network microtask.
  // Invalidate synchronously on unmount before that result can navigate or
  // write prompt/review-solicitation state for a closed visit.
  React.useLayoutEffect(() => () => { visitRef.current.generation += 1; }, []);
  type Visit = { generation: number; actorKey: string; identity: FeedbackIdentity };
  const [jobVisit, setJobVisit] = useState<Visit | null>(null);
  const reviewAskVisitRef = useRef<Visit | null>(null);
  const captureVisit = (target: FeedbackModalJob): Visit => ({
    generation: visitRef.current.generation,
    actorKey,
    identity: {
      jobId: Number(target.id),
      locationId: Number(target.location?.id || target.locationId),
      hygienistId: actor.hygienistId,
    },
  });
  const isCurrentVisit = (visit: Visit) => visitRef.current.generation === visit.generation
    && visit.actorKey === actorKey && isRouteCurrent();

  const feedbackFormOpen = !isLoadingJob
    && !!job
    && !hydrateError
    && shouldRenderTalentFeedbackForm(showSuccessModal, formSubmitted, viewOnly);

  useEffect(() => {
    setChatbaseUiHidden(feedbackFormOpen);
    return () => setChatbaseUiHidden(false);
  }, [feedbackFormOpen]);

  const resetForm = React.useCallback(() => {
    setOverallRating(null);
    setRatingTouched(false);
    setSkillRatings({ friendly: null, infection: null, tools: null, setup: null });
    setAdditionalFeedback('');
    setFutureCollab(null);
    setIsSubmitting(false);
    setIsSkipping(false);
    setShowSuccessModal(false);
    setFormSubmitted(false);
    setHydrateError(null);
    setSubmissionMessage(null);
    setAcceptedMessage(null);
    setSubmissionPhase('checking');
    continuedRef.current = false;
  }, []);

  const returnToHomeAfterFeedback = (visit: Visit) => {
    if (!isCurrentVisit(visit)) return;
    // T2-6899: show review ask once at end of pending queue, before home.
    if (latchedReviewAskRef.current && !showReviewAskModal) {
      reviewAskVisitRef.current = visit;
      setShowReviewAskModal(true);
      return;
    }
    setTalentFeedbackInQueue(false);
    markTalentFeedbackHomePromptSuppressed();
    try {
      safeLocalStorage.removeItem('pageData');
      safeLocalStorage.removeItem('feedbackNav');
      safeLocalStorage.removeItem('pendingFeedbackJobId');
    } catch { /* ignore */ }
    navigateToTalentHomeAfterFeedback(history);
  };

  const handleReviewAskDone = () => {
    if (!reviewAskVisitRef.current || !isCurrentVisit(reviewAskVisitRef.current)) return;
    setShowReviewAskModal(false);
    latchedReviewAskRef.current = null;
    setLatchedReviewAsk(null);
    setTalentFeedbackInQueue(false);
    markTalentFeedbackHomePromptSuppressed();
    try {
      safeLocalStorage.removeItem('pageData');
      safeLocalStorage.removeItem('feedbackNav');
      safeLocalStorage.removeItem('pendingFeedbackJobId');
    } catch { /* ignore */ }
    navigateToTalentHomeAfterFeedback(history);
  };

  const returnToCompletedShift = (visit: Visit) => {
    if (!isCurrentVisit(visit)) return;
    setTalentFeedbackInQueue(false);
    history.replace({
      pathname: 'completeJob',
      state: { jobId: visit.identity.jobId },
    });
  };

  const hydrateFromExistingReview = async (target: FeedbackModalJob, visit: Visit) => {
    try {
      const review = await Api.getTalentReview(Number(target.id));
      if (!isCurrentVisit(visit)) return;
      if (!acceptedFeedback(review, visit.identity)) {
        setHydrateError('Unable to confirm existing feedback. Please try again.');
        return;
      }
      const collabReverse: Record<number, FutureCollaboration> = { 1: 'love', 2: 'fine', 3: 'prefer-not' };
      const overall = normalizeRating(review.overallRating);
      setOverallRating(overall > 0 ? overall : null);
      const toSkill = (value: unknown) => Number(value) > 0 ? Number(value) : null;
      setSkillRatings({ friendly: toSkill(review.friendlinessRating), infection: toSkill(review.infectionControlRating),
        tools: toSkill(review.equipmentRating), setup: toSkill(review.preparationRating) });
      setAdditionalFeedback(review.additionalFeedback || review.comments || '');
      setFutureCollab(review.collaborationPreference != null ? collabReverse[Number(review.collaborationPreference)] || null : null);
      setHydrateError(null);
    } catch {
      if (isCurrentVisit(visit)) setHydrateError('Unable to load existing feedback. Please try again.');
    }
  };

  const getIncomingFeedbackState = React.useCallback(() => {
    let jobData: FeedbackModalJob | null = null;
    let jobId: number | null = null;
    if (location.state && typeof location.state === 'object' && 'job' in location.state) {
      jobData = location.state.job as FeedbackModalJob;
    }
    if (location.state && typeof location.state === 'object' && 'jobId' in location.state) {
      const nextJobId = Number((location.state as { jobId?: number | string }).jobId);
      if (!Number.isNaN(nextJobId) && nextJobId > 0) {
        jobId = nextJobId;
      }
    }
    if (!jobData || !jobId) {
      try {
        const pd = safeLocalStorage.getItem('pageData');
        if (pd) {
          const p = JSON.parse(pd);
          if (p?.page === 'hygienistFeedback') {
            if (p.job) {
              jobData = p.job;
            }
            const nextJobId = Number(p.jobId ?? p.job?.id);
            if (!Number.isNaN(nextJobId) && nextJobId > 0) {
              jobId = nextJobId;
            }
          }
          safeLocalStorage.removeItem('pageData');
        }
      } catch { safeLocalStorage.removeItem('pageData'); }
    }
    if (!jobId && jobData?.id) {
      const nextJobId = Number(jobData.id);
      if (!Number.isNaN(nextJobId) && nextJobId > 0) {
        jobId = nextJobId;
      }
    }
    return { jobData, jobId };
  }, [location.state]);

  const UNKNOWN_MESSAGE = 'We couldn’t confirm whether your feedback was saved. Refresh its status before submitting again.';

  function showUnknown(visit: Visit) {
    if (!isCurrentVisit(visit)) return;
    operationRef.current = false;
    setIsSubmitting(false);
    setSubmissionPhase('unknown');
    setSubmissionMessage(UNKNOWN_MESSAGE);
  }

  async function loadNextPendingReview(visit: Visit): Promise<boolean> {
    try {
      const pending = await Api.getPendingTalentReviews();
      if (!isCurrentVisit(visit)) return true;
      const next = selectNextPendingTalentReview(normalizePendingTalentReviewResponse(pending), visit.identity.jobId);
      if (!next?.jobId) { setTalentFeedbackInQueue(false); return false; }
      setIsLoadingJob(true);
      const fullJob = await Api.getJobDetails(Number(next.jobId));
      if (!isCurrentVisit(visit)) return true;
      if (!fullJob?.id || Number(fullJob.hygienistId) !== actor.hygienistId) {
        setIsLoadingJob(false); setTalentFeedbackInQueue(false); return false;
      }
      visitRef.current.generation += 1;
      resetForm();
      const target = fullJob as FeedbackModalJob;
      const nextVisit = captureVisit(target);
      setJob(target);
      setJobVisit(nextVisit);
      markTalentFeedbackHomePromptShown(Number(target.id));
      setTalentFeedbackInQueue(true);
      await reconcileStatus(target, nextVisit, false);
      if (isCurrentVisit(nextVisit)) setIsLoadingJob(false);
      return true;
    } catch {
      if (!isCurrentVisit(visit)) return true;
      setIsLoadingJob(false);
      setTalentFeedbackInQueue(false);
      return false;
    }
  }

  async function continueAccepted(visit: Visit) {
    if (!isCurrentVisit(visit) || continuedRef.current) return;
    continuedRef.current = true;
    setShowSuccessModal(false);
    const hasNext = await loadNextPendingReview(visit);
    if (!hasNext && isCurrentVisit(visit)) returnToHomeAfterFeedback(visit);
  }

  async function acceptSubmission(result: AcceptedFeedback, visit: Visit, recovered: boolean) {
    if (!isCurrentVisit(visit)) return;
    if (result.reviewAsk?.solicitationId && !latchedReviewAskRef.current) {
      latchedReviewAskRef.current = result.reviewAsk as ReviewAskPayload;
      setLatchedReviewAsk(result.reviewAsk as ReviewAskPayload);
    }
    setIsSubmitting(false);
    setSubmissionPhase('accepted');
    setSubmissionMessage(null);
    setFormSubmitted(true);
    markTalentFeedbackHomePromptShown(visit.identity.jobId);
    markTalentFeedbackHomePromptSuppressed();
    const deliveryMessage = feedbackDeliveryMessage(result.supportDelivery);
    if (deliveryMessage || recovered) {
      setAcceptedMessage(deliveryMessage || 'Feedback for this shift was already submitted. Your saved feedback has not been changed. You can continue.');
      setShowSuccessModal(true);
      return;
    }
    await continueAccepted(visit);
  }

  async function reconcileStatus(target: FeedbackModalJob, visit: Visit, afterSubmit: boolean) {
    if (!isCurrentVisit(visit)) return;
    operationRef.current = true;
    setSubmissionPhase('checking');
    try {
      const response = await Api.getTalentReview(visit.identity.jobId);
      if (!isCurrentVisit(visit)) return;
      const accepted = acceptedFeedback(response, visit.identity);
      if (!accepted) { showUnknown(visit); return; }
      await acceptSubmission(accepted, visit, true);
    } catch (error) {
      if (!isCurrentVisit(visit)) return;
      if (definitelyUnsubmitted(error, visit.identity)) {
        operationRef.current = false;
        setIsSubmitting(false);
        setFormSubmitted(false);
        setSubmissionPhase('idle');
        setSubmissionMessage(afterSubmit ? 'Your feedback has not been saved. Your choices are still here. Please try submitting again.' : null);
        if (!canCompleteTalentSurvey(target as any)) {
          setHydrateError('No feedback was submitted within 30 days of this shift being completed.');
        }
      } else showUnknown(visit);
    }
  }

  useEffect(() => {
    const generation = visitRef.current.generation;
    operationRef.current = false;
    resetForm();
    setIsLoadingJob(true);
    setJob(null);
    setJobVisit(null);
    const load = async () => {
      if (!isRouteCurrent()) return;
      const { jobData, jobId } = getIncomingFeedbackState();
      let target = jobData;
      if ((!hasTalentFeedbackJobPayload(target) || Number((target as any)?.hygienistId) !== actor.hygienistId) && jobId) {
        try { target = await Api.getJobDetails(jobId) as FeedbackModalJob; } catch { target = null; }
      }
      if (generation !== visitRef.current.generation || !isRouteCurrent()) return;
      if (!target?.id || Number((target as any).hygienistId) !== actor.hygienistId) {
        returnToTalentCalendar(() => generation === visitRef.current.generation && isRouteCurrent()); return;
      }
      setJob(target);
      const visit = captureVisit(target);
      setJobVisit(visit);
      if (viewOnly) await hydrateFromExistingReview(target, visit);
      else await reconcileStatus(target, visit, false);
      if (isCurrentVisit(visit)) setIsLoadingJob(false);
    };
    void load();
    return () => { visitRef.current.generation += 1; };
    // Each route/account visit owns all of its asynchronous work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, getIncomingFeedbackState, resetForm, viewOnly, actorKey]);

  const handleSubmit = async () => {
    if (viewOnly || !job || !jobVisit || !isCurrentVisit(jobVisit) || operationRef.current || submissionPhase !== 'idle' || formSubmitted || isSkipping) return;
    if (!overallRating) { setRatingTouched(true); return; }
    if (!canCompleteTalentSurvey(job as any)) {
      setHydrateError('No feedback was submitted within 30 days of this shift being completed.');
      return;
    }
    const visit = jobVisit;
    if (!isCurrentVisit(visit)) return;
    operationRef.current = true;
    setIsSubmitting(true);
    setSubmissionPhase('submitting');
    setSubmissionMessage(null);
    try {
      const collabMap: Record<string, 1 | 2 | 3> = { love: 1, fine: 2, 'prefer-not': 3 };
      const response = await Api.submitTalentReview({
        jobId: visit.identity.jobId, locationId: visit.identity.locationId, overallRating,
        friendlinessRating: skillRatings.friendly || 0, friendlinessComments: '',
        infectionControlRating: skillRatings.infection || 0, infectionControlComments: '',
        equipmentRating: skillRatings.tools || 0, equipmentComments: '',
        preparationRating: skillRatings.setup || 0, preparationComments: '',
        additionalFeedback: additionalFeedback || '', collaborationPreference: futureCollab ? collabMap[futureCollab] : null,
        ...(latchedReviewAskRef.current?.solicitationId ? { reviewSolicitationId: latchedReviewAskRef.current.solicitationId } : {}),
      });
      if (!isCurrentVisit(visit)) return;
      const accepted = acceptedFeedback(response, visit.identity);
      if (accepted) await acceptSubmission(accepted, visit, false);
      else await reconcileStatus(job, visit, true);
    } catch {
      if (isCurrentVisit(visit)) await reconcileStatus(job, visit, true);
    }
  };

  const handleRefreshStatus = async () => {
    if (!job || !jobVisit || !isCurrentVisit(jobVisit) || operationRef.current || submissionPhase !== 'unknown') return;
    await reconcileStatus(job, jobVisit, true);
  };

  const handleSkipForNow = async () => {
    if (viewOnly || !job || !jobVisit || operationRef.current || submissionPhase !== 'idle' || isSkipping) return;
    const visit = jobVisit;
    if (!isCurrentVisit(visit)) return;
    operationRef.current = true;
    setIsSkipping(true);
    try {
      await Api.skipTalentReview({ jobId: visit.identity.jobId });
      if (!isCurrentVisit(visit)) return;
      markTalentFeedbackHomePromptShown(visit.identity.jobId);
      markTalentFeedbackHomePromptSuppressed();
      const hasNext = await loadNextPendingReview(visit);
      if (!hasNext && isCurrentVisit(visit)) returnToHomeAfterFeedback(visit);
    } catch {
      if (isCurrentVisit(visit)) alert('Error skipping feedback. Please try again.');
    } finally {
      if (isCurrentVisit(visit)) { operationRef.current = false; setIsSkipping(false); }
    }
  };

  const handleSuccessModalClose = () => {
    if (jobVisit) void continueAccepted(jobVisit);
  };

  if (isLoadingJob || !job || !jobVisit || !isCurrentVisit(jobVisit)) {
    return <FixedLoader loading />;
  }

  if (hydrateError) {
    return (
      <FixedError
        error={new ApiError(400, hydrateError)}
        onDismiss={() => {
          if (!isCurrentVisit(jobVisit)) return;
          if (viewOnly) { setHydrateError(null); returnToCompletedShift(jobVisit); }
          else returnToHomeAfterFeedback(jobVisit);
        }}
      />
    );
  }

  const shiftOnDate = job.shifts?.[0]?.shiftDate
    ? moment(job.shifts[0].shiftDate).format('MMMM D, YYYY')
    : '';
  const officeName = job.location?.practiceName || job.officeName || 'the office';
  const inputsLocked = viewOnly || submissionPhase !== 'idle' || isSkipping;
  const isDisabled = inputsLocked || isTalentFeedbackSubmitDisabled(overallRating, isSubmitting, formSubmitted);
  const mainTitle = shiftOnDate
    ? `How was your shift on ${shiftOnDate} with ${officeName}?`
    : `How was your shift with ${officeName}?`;

  return (
    <>
      <WiderModalStyle />
      {shouldRenderTalentFeedbackForm(showSuccessModal, formSubmitted, viewOnly) && (
        <SimpleModal isOpen={true} className="feedback-talent-modal" disableBackdropClose disableEscapeKeyDown>
          <ModalContent className="no-fastclick">
            <ModalHeaderBand>
              <ModalHeaderInner>
                <HeaderLeft>
                  <CheckCircleOutlineIcon sx={{ color: '#8d94a3', fontSize: 20 }} />
                  <HeaderTitle>Feedback</HeaderTitle>
                </HeaderLeft>
              </ModalHeaderInner>
            </ModalHeaderBand>

            <ModalBody>
              <QuestionTitle>{mainTitle}</QuestionTitle>

              <StarRow role="radiogroup" aria-label="Overall rating" aria-disabled={inputsLocked || undefined}>
                {[1, 2, 3, 4, 5].map((s, i) => {
                  const active = overallRating !== null && s <= overallRating;
                  const selected = overallRating === s;
                  return (
                    <StarButton
                      key={s}
                      ref={(el: HTMLDivElement | null) => { starRefs.current[i] = el; }}
                      $active={active}
                      $readOnly={inputsLocked}
                      role="radio"
                      aria-checked={selected}
                      aria-disabled={inputsLocked || undefined}
                      aria-label={`${s} star${s > 1 ? 's' : ''}`}
                      tabIndex={inputsLocked ? -1 : (selected || (!overallRating && s === 1) ? 0 : -1)}
                      onClick={inputsLocked ? undefined : () => { setOverallRating(s); setRatingTouched(true); }}
                      onKeyDown={inputsLocked ? undefined : (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOverallRating(s); setRatingTouched(true); }
                        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                          e.preventDefault(); const next = Math.min(4, i + 1); setOverallRating(next + 1); setRatingTouched(true); starRefs.current[next]?.focus();
                        }
                        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                          e.preventDefault(); const prev = Math.max(0, i - 1); setOverallRating(prev + 1); setRatingTouched(true); starRefs.current[prev]?.focus();
                        }
                      }}
                    >
                      <StarRoundedIcon sx={{ fontSize: 34, color: active ? '#f5a623' : '#e6ebf2' }} />
                    </StarButton>
                  );
                })}
              </StarRow>
              <LabelsRow>
                <ScaleLabel>Poor</ScaleLabel>
                <ScaleLabel>Great</ScaleLabel>
              </LabelsRow>
              {!viewOnly && !overallRating && <ErrorText>Please select a rating</ErrorText>}

              <Divider />

              <CollabTitle>Please tell us if you would like to work with {officeName} again</CollabTitle>
              <CollabRow role="radiogroup" aria-label="Future collaboration preference" aria-disabled={inputsLocked || undefined}>
                {collabOptions.map((opt, idx) => {
                  const labels = { love: 'Mark as one of my Favourites', fine: 'This office was fine', 'prefer-not': 'Please block this office from my shift lists' };
                  // Mobile (Cordova/WKWebView) can't render the emoji glyphs → MUI icons; web keeps the emojis.
                  const icons = native
                    ? {
                        love: <FavoriteRoundedIcon style={{ fontSize: 24, color: '#FB4E66' }} />,
                        fine: <SentimentSatisfiedAltRoundedIcon style={{ fontSize: 24, color: '#2E9E5B' }} />,
                        'prefer-not': <SentimentDissatisfiedRoundedIcon style={{ fontSize: 24, color: '#FB7672' }} />,
                      }
                    : { love: '♥️', fine: '😊', 'prefer-not': '😔' };
                  const isSelected = futureCollab === opt;
                  return (
                    <CollabCard
                      key={opt}
                      ref={(el: HTMLDivElement | null) => { collabRefs.current[idx] = el; }}
                      $selected={isSelected}
                      $readOnly={inputsLocked}
                      role="radio"
                      aria-checked={isSelected}
                      aria-disabled={inputsLocked || undefined}
                      tabIndex={inputsLocked ? -1 : (isSelected || (!futureCollab && idx === 0) ? 0 : -1)}
                      onClick={inputsLocked ? undefined : () => setFutureCollab(opt)}
                      onKeyDown={inputsLocked ? undefined : (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFutureCollab(opt); }
                        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                          e.preventDefault(); const next = (idx + 1) % 3; setFutureCollab(collabOptions[next]); collabRefs.current[next]?.focus();
                        }
                        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                          e.preventDefault(); const prev = (idx + 2) % 3; setFutureCollab(collabOptions[prev]); collabRefs.current[prev]?.focus();
                        }
                      }}
                    >
                      <OptionIcon>{icons[opt!]}</OptionIcon>
                      <CardLabel $selected={isSelected}>{labels[opt!]}</CardLabel>
                    </CollabCard>
                  );
                })}
              </CollabRow>
              {futureCollab === 'prefer-not' && (
                <BlockWarning>You will no longer see their shifts.</BlockWarning>
              )}

              <Divider />

              <WriteReviewLabel>Write Review</WriteReviewLabel>
              <ReviewTextarea
                rows={4}
                placeholder={viewOnly ? '' : `Write notes here, this is internal information only and ${officeName} won't see it`}
                value={additionalFeedback}
                onChange={inputsLocked ? undefined : (e) => setAdditionalFeedback(e.target.value)}
                readOnly={inputsLocked}
              />

              <Divider />

              <SectionLabel>Additional Feedback</SectionLabel>
              {talentSkills.map(({ key, label, icon, bg, color }) => (
                <SkillCard key={key}>
                  <IconCircle $bg={bg} $color={color}>{icon}</IconCircle>
                  <SkillName>{label}</SkillName>
                  <Rating
                    size="medium"
                    value={skillRatings[key]}
                    readOnly={inputsLocked}
                    icon={<StarRoundedIcon fontSize="inherit" />}
                    emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                    onChange={inputsLocked ? undefined : (_, v) => setSkillRatings(prev => ({ ...prev, [key]: v }))}
                    sx={{
                      ml: 'auto',
                      flexShrink: 0,
                      fontSize: '1.55rem',
                      gap: '2px',
                      '& .MuiRating-iconFilled': { color: '#f5a623' },
                      '& .MuiRating-iconEmpty': { color: '#e6ebf2' },
                    }}
                  />
                </SkillCard>
              ))}

              {submissionMessage && <p role="status" data-testid="talent-feedback-status">{submissionMessage}</p>}
              {submissionPhase === 'unknown' && !viewOnly && (
                <SubmitBtn id="talent-feedback-refresh-status-button" data-testid="talent-feedback-refresh-status-button"
                  aria-label="Refresh status" onClick={handleRefreshStatus}>Refresh status</SubmitBtn>
              )}
              <SubmitContainer>
                {viewOnly ? (
                  <SubmitBtn
                    id="talent-done-feedback-view-button"
                    data-testid="talent-done-feedback-view-button"
                    aria-label="Done"
                    content-desc="talent-done-feedback-view-button"
                    onClick={() => returnToCompletedShift(jobVisit)}
                  >
                    Done
                  </SubmitBtn>
                ) : (
                  <>
                    <SubmitBtn
                      id="talent-feedback-submit-button"
                      data-testid="talent-feedback-submit-button"
                      aria-label="Submit"
                      onClick={handleSubmit}
                      disabled={isDisabled}
                    >
                      {submissionPhase === 'checking' ? 'Checking status...' : isSubmitting ? 'Submitting...' : 'Submit'}
                    </SubmitBtn>
                    <SkipRow>
                      <SkipChevronBtn
                        type="button"
                        onClick={handleSkipForNow}
                        disabled={inputsLocked || formSubmitted}
                        aria-label="Skip for now"
                        title="Skip for now"
                        data-testid="talent-feedback-skip-button"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </SkipChevronBtn>
                    </SkipRow>
                  </>
                )}
              </SubmitContainer>
            </ModalBody>
          </ModalContent>
        </SimpleModal>
      )}

      <SuccessModal open={showSuccessModal} onClose={handleSuccessModalClose} message={acceptedMessage} />
      <ReviewAskModal
        open={showReviewAskModal}
        reviewAsk={latchedReviewAsk}
        role="hygienist"
        onDone={handleReviewAskDone}
        isCurrent={() => !!reviewAskVisitRef.current && isCurrentVisit(reviewAskVisitRef.current)}
      />
    </>
  );
}
